import type {
  MandatoryTreeMember,
} from './MandatoryTreeMutationAuthority';

export type TreeMutationProposal =
  | {
      kind: 'insert-member';
      member: MandatoryTreeMember;
    }
  | {
      kind: 'update-member';
      memberId: string;
      patch: Partial<MandatoryTreeMember>;
    }
  | {
      kind: 'delete-member';
      memberId: string;
    }
  | {
      kind: 'restore-member';
      member: MandatoryTreeMember;
      childIds?: string[];
      spouseReferenceIds?: string[];
    }
  | {
      kind: 'set-parents';
      memberId: string;
      parentIds: string[];
    }
  | {
      kind: 'set-spouse';
      memberId: string;
      spouseId: string | null;
    }
  | {
      kind: 'claim-member';
      memberId: string;
      actingUserId: string;
    }
  | {
      kind: 'release-claim';
      memberId: string;
    };

export interface ProposedTreeMutation {
  after: MandatoryTreeMember[];
  affectedMemberIds: string[];
  proposedViewerMemberId: string | null | undefined;
}

function cloneMember(
  member: MandatoryTreeMember,
): MandatoryTreeMember {
  return {
    ...member,
    parentIds: [...(member.parentIds ?? [])],
  };
}

function cloneMembers(
  members: MandatoryTreeMember[],
): MandatoryTreeMember[] {
  return members.map(cloneMember);
}

function uniqueIds(ids: string[]): string[] {
  return [...new Set(ids.filter(Boolean))];
}

function requireMember(
  members: MandatoryTreeMember[],
  memberId: string,
): MandatoryTreeMember {
  const member = members.find(item => item.id === memberId);

  if (!member) {
    throw new Error(`proposal-member-not-found:${memberId}`);
  }

  return member;
}

function collectDescendantIds(
  members: MandatoryTreeMember[],
  ancestorId: string,
): string[] {
  const childrenByParent = new Map<string, string[]>();

  for (const member of members) {
    for (const parentId of member.parentIds ?? []) {
      const children =
        childrenByParent.get(parentId) ?? [];

      children.push(member.id);
      childrenByParent.set(parentId, children);
    }
  }

  const descendants = new Set<string>();
  const queue = [
    ...(childrenByParent.get(ancestorId) ?? []),
  ];

  while (queue.length > 0) {
    const current = queue.shift()!;

    if (descendants.has(current)) continue;

    descendants.add(current);

    queue.push(
      ...(childrenByParent.get(current) ?? []),
    );
  }

  return [...descendants].sort();
}

function clearReciprocalSpouse(
  members: MandatoryTreeMember[],
  memberId: string,
  affected: Set<string>,
): void {
  const member = requireMember(members, memberId);
  const previousSpouseId = member.spouseId ?? null;

  member.spouseId = null;
  affected.add(memberId);

  if (!previousSpouseId) return;

  const previousSpouse = members.find(
    item => item.id === previousSpouseId,
  );

  if (
    previousSpouse &&
    previousSpouse.spouseId === memberId
  ) {
    previousSpouse.spouseId = null;
    affected.add(previousSpouse.id);
  }
}

export function buildProposedTreeMutation(
  before: MandatoryTreeMember[],
  proposal: TreeMutationProposal,
  viewerMemberId: string | null,
): ProposedTreeMutation {
  const after = cloneMembers(before);
  const affected = new Set<string>();
  let proposedViewerMemberId:
    | string
    | null
    | undefined = viewerMemberId;

  switch (proposal.kind) {
    case 'insert-member': {
      if (after.some(item => item.id === proposal.member.id)) {
        throw new Error(
          `proposal-duplicate-member-id:${proposal.member.id}`,
        );
      }

      after.push(cloneMember(proposal.member));
      affected.add(proposal.member.id);
      break;
    }

    case 'update-member': {
      const member = requireMember(
        after,
        proposal.memberId,
      );

      Object.assign(member, proposal.patch);

      if (proposal.patch.parentIds) {
        member.parentIds = uniqueIds(
          proposal.patch.parentIds,
        );
      }

      affected.add(member.id);
      break;
    }

    case 'delete-member': {
      const member = requireMember(
        after,
        proposal.memberId,
      );

      const attachedChildIds = after
        .filter(candidate =>
          (candidate.parentIds ?? []).includes(
            member.id,
          ),
        )
        .map(candidate => candidate.id)
        .sort();

      if (attachedChildIds.length > 0) {
        throw new Error(
          `proposal-parent-is-attached:${member.id}:${attachedChildIds.join(',')}`,
        );
      }

      member.profileState = 'deleted';
      affected.add(member.id);

      for (const candidate of after) {
        if (candidate.spouseId === member.id) {
          candidate.spouseId = null;
          affected.add(candidate.id);
        }
      }

      break;
    }

    case 'restore-member': {
      const existingIndex = after.findIndex(
        item => item.id === proposal.member.id,
      );

      const restored: MandatoryTreeMember = {
        ...cloneMember(proposal.member),
        profileState:
          proposal.member.profileState === 'deleted'
            ? 'placeholder'
            : proposal.member.profileState,
      };

      if (existingIndex >= 0) {
        after[existingIndex] = restored;
      } else {
        after.push(restored);
      }

      affected.add(restored.id);

      for (const childId of uniqueIds(
        proposal.childIds ?? [],
      )) {
        const child = requireMember(after, childId);

        child.parentIds = uniqueIds([
          ...(child.parentIds ?? []),
          restored.id,
        ]);

        affected.add(child.id);
      }

      const spouseReferences = uniqueIds(
        proposal.spouseReferenceIds ?? [],
      );

      if (
        restored.spouseId &&
        spouseReferences.includes(restored.spouseId)
      ) {
        const spouse = requireMember(
          after,
          restored.spouseId,
        );

        if (
          !spouse.spouseId ||
          spouse.spouseId === restored.id
        ) {
          spouse.spouseId = restored.id;
          restored.spouseId = spouse.id;
          affected.add(spouse.id);
        }
      }

      break;
    }

    case 'set-parents': {
      const member = requireMember(
        after,
        proposal.memberId,
      );

      const existingParentIds = uniqueIds(
        member.parentIds ?? [],
      );

      const proposedParentIds = uniqueIds(
        proposal.parentIds,
      );

      const removedParentIds =
        existingParentIds.filter(
          parentId =>
            !proposedParentIds.includes(parentId),
        );

      if (removedParentIds.length > 0) {
        throw new Error(
          `proposal-parent-edge-removal:${member.id}:${removedParentIds.join(',')}`,
        );
      }

      member.parentIds = proposedParentIds;

      affected.add(member.id);

      for (
        const descendantId of collectDescendantIds(
          after,
          member.id,
        )
      ) {
        affected.add(descendantId);
      }

      break;
    }

    case 'set-spouse': {
      clearReciprocalSpouse(
        after,
        proposal.memberId,
        affected,
      );

      if (proposal.spouseId) {
        clearReciprocalSpouse(
          after,
          proposal.spouseId,
          affected,
        );

        const member = requireMember(
          after,
          proposal.memberId,
        );

        const spouse = requireMember(
          after,
          proposal.spouseId,
        );

        member.spouseId = spouse.id;
        spouse.spouseId = member.id;

        affected.add(member.id);
        affected.add(spouse.id);
      }

      break;
    }

    case 'claim-member': {
      const member = requireMember(
        after,
        proposal.memberId,
      );

      member.claimedByUserId =
        proposal.actingUserId;

      member.profileState = 'claimed';

      affected.add(member.id);

      proposedViewerMemberId = member.id;
      break;
    }

    case 'release-claim': {
      const member = requireMember(
        after,
        proposal.memberId,
      );

      member.claimedByUserId = null;
      member.profileState = 'placeholder';

      affected.add(member.id);

      if (viewerMemberId === member.id) {
        proposedViewerMemberId = null;
      }

      break;
    }
  }

  return {
    after,
    affectedMemberIds: [...affected].sort(),
    proposedViewerMemberId,
  };
}
